import React, { createContext, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { processEnvs } from './config/processEnvs';

import { createUseStyles } from 'react-jss';
import './index.css';
import App from './App';

import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';

import { createTheme, ThemeProvider } from '@mui/material/styles';

import { BrowserRouter as Router } from 'react-router-dom';

import { UserProvider } from './providers/User/UserProvider';
import { NotificationProvider } from './providers/Notification/NotificationProvider';
import axiosRetry from 'axios-retry';

import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import axios from 'axios';
import { DEFAULT_TIMEOUT } from '../../shared/constants';
import releaseTime from '../../shared/releaseTime.json';
import { formatReleaseTime } from '../../shared/formatReleaseTime';
import { AccountInfo } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import {
  getRuntimeConfig,
  setRuntimeConfig,
} from './config/runtimeConfig';
import { getMsalInstance } from './auth';
import { AuthErrorProvider } from './AuthErrorProvider';

// axios instance for backend API calls
export const api = axios.create({
  baseURL: processEnvs.BACKEND_URI,
  timeout: DEFAULT_TIMEOUT,
});

export const adminTableClasses = createUseStyles({
  headerCell: {
    backgroundColor: '#e0e0e0',
  },
  childHeaderCell: {
    backgroundColor: '#fafafa',
  },

  editableCell: {
    color: '#000',
    borderRadius: '1px',
    fontWeight: 'bold',
  },
});

export const itemStatusCellClasses = createUseStyles({
  itemCellIconStyle: {
    cursor: 'pointer',
    marginTop: 6,
    fontWeight: 'bold',
    fontSize: 16,
    marginRight: 8,
    textAlign: 'center',
  },

  itemCellDeleteIconStyle: {
    marginTop: 6,
    fontWeight: 'bold',
    fontSize: 16,
    textAlign: 'center',
  },

  statusDialogIconStyle: {
    fontWeight: 'bold',
    textAlign: 'center',
    fontSize: 16,
    marginRight: 8,
  },
});

const theme = createTheme({
  typography: {
    fontFamily: 'Boing',
  },
  palette: {
    primary: {
      light: '#194693',
      main: '#003287',
      dark: '#002D79',
      contrastText: '#fff',
    },
    secondary: {
      light: '#ffdf19',
      main: '#ffdc00',
      dark: '#e5c600',
      contrastText: '#fff',
    },
    text: {
      primary: '#000000de',
      secondary: '#0000008a',
    },
  },
});

export const groupedTableClasses = createUseStyles({
  headerCell: {
    backgroundColor: '#e0e0e0',
  },
  childHeaderCell: {
    backgroundColor: '#fafafa',
    fontWeight: 'bold',
  },
  priceCell: {
    fontWeight: 'bold',
    backgroundColor: '#ECF5EB',
  },
  orangePriceCell: {
    backgroundColor: theme.palette.warning.light,
    fontWeight: 'bold',
  },
  redPriceCell: {
    backgroundColor: theme.palette.error.light,
    fontWeight: 'bold',
  },
  pinkPercentCell: {
    backgroundColor: '#f48fb1',
    fontWeight: 'bold',
  },
  zeroPriceCell: {
    backgroundColor: theme.palette.warning.dark,
    fontWeight: 'bold',
  },
  criticalPriceCell: {
    backgroundColor: theme.palette.error.light,
    fontWeight: 'bold',
  },
  invalidPriceThresholdCell: {
    backgroundColor: theme.palette.warning.dark,
    fontWeight: 'bold',
  },
  editableCell: {
    backgroundColor: '#fff',
  },
});
let bootstrapAuthError: string | null = null;

export const AuthErrorContext = createContext({
  error: bootstrapAuthError,
  setError: (msg: string | null) => {},
});

const parseB2CError = (error: any): string => {
  const raw = error?.errorMessage || error?.message || String(error);

  // Normalize
  const msg = raw.toLowerCase();

  if (msg.includes('user not found')) {
    return 'Käyttäjätiliä ei löytynyt';
  }

  if (msg.includes('User is disabled')) {
    return 'Käyttäjätili ei ole aktivoitu';
  }

  if (msg.includes('User is missing UserId')) {
    return 'Käyttäjätililtä puuttuu UserId';
  }

  if (msg.includes('User is missing a user role')) {
    return 'Käyttäjältä puuttuu rooli';
  }

  return 'Kirjautuminen epäonnistui. Yritä myöhemmin uudestaan';
};

const init = async () => {
  const res = await fetch(`${import.meta.env.VITE_BACKEND_URI}/envs`);
  const config = await res.json();

  setRuntimeConfig(config);

  const queryClient = new QueryClient();

  const msalInstance = getMsalInstance();

  const CLIENT_ID = getRuntimeConfig().VITE_AZURE_B2C_CLIENT_ID;
  const TENANT_NAME = getRuntimeConfig().VITE_AZURE_B2C_TENANT_NAME;

  await msalInstance.initialize();
  try {
    await msalInstance.handleRedirectPromise();
  } catch (error: any) {
    bootstrapAuthError = parseB2CError(error);
    console.error('MSAL login error:', error.message);
  }

  // Default to using the first account if no account is active on page load
  if (
    !msalInstance.getActiveAccount() &&
    msalInstance.getAllAccounts().length > 0
  ) {
    // Account selection logic is app dependent. Adjust as needed for different use cases.
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
  }

  const { pathname } = window.location;

  // Set a redirect path to localstorage, which user is taken to after login
  // Consider setting this to query params so user can see that he is beign redirected
  if (pathname !== '/' && pathname !== '/login') {
    localStorage.setItem(`redirect`, pathname);
  }

  // Axios interceptor to attach token
  api.interceptors.request.use(
    async (config) => {
      try {
        const accounts = msalInstance.getAllAccounts(); // Get logged-in accounts
        if (accounts.length > 0) {
          const account: AccountInfo = accounts[0]; // Get the first account
          let tokenResponse;
          const request = {
            scopes: [
              `https://${TENANT_NAME}.onmicrosoft.com/${CLIENT_ID}/user.read`,
            ], // API scope
            account,
          };
          try {
            tokenResponse =
              await msalInstance.acquireTokenSilent(request);
          } catch (error) {
            console.log('error', error);
            tokenResponse =
              await msalInstance.acquireTokenRedirect(request);
          }

          config.headers['Authorization'] =
            `Bearer ${tokenResponse?.accessToken}`;
        }
      } catch (error) {
        console.error('Token acquisition error:', error);
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  // Retry status request if server timeouts, to avoid user being redirected to logout for poor server performance
  axiosRetry(api, {
    retries: 10,
    retryDelay: (retryCount) => retryCount * 1000,
    retryCondition: (error) => {
      const isStatusEndpoint =
        !!error.config &&
        !!error.config.url &&
        error.config.url.includes('/status');

      const isTimeout =
        error.code === 'ECONNABORTED' ||
        (error !== undefined &&
          error.response !== undefined &&
          error.response?.status === 503);

      return isTimeout && isStatusEndpoint;
    },
  });

  if (processEnvs.ENABLE_SENTRY === 'true') {
    Sentry.init({
      dsn: 'https://6e32f9183f2a439dbea39acca0b54a1c@o318763.ingest.sentry.io/5956861',
      integrations: [new Integrations.BrowserTracing()],
      tracesSampleRate: 1.0,
      release: formatReleaseTime(releaseTime.latestAppVersion),
    });
  }

  const container = document.getElementById('root');
  const root = createRoot(container!);
  root.render(
    <>
      {processEnvs.USE_STRICT_MODE ? (
        <React.StrictMode>
          <AuthErrorProvider initialError={bootstrapAuthError}>
            <ThemeProvider theme={theme}>
              <NotificationProvider>
                <UserProvider>
                  <Router>
                    <App />
                  </Router>
                </UserProvider>
              </NotificationProvider>
            </ThemeProvider>
          </AuthErrorProvider>
        </React.StrictMode>
      ) : (
        <>
          <AuthErrorProvider initialError={bootstrapAuthError}>
            <ThemeProvider theme={theme}>
              <NotificationProvider>
                <MsalProvider instance={msalInstance}>
                  <UserProvider>
                    <Router>
                      <QueryClientProvider client={queryClient}>
                        <App />
                      </QueryClientProvider>
                    </Router>
                  </UserProvider>
                </MsalProvider>
              </NotificationProvider>
            </ThemeProvider>
          </AuthErrorProvider>
        </>
      )}
    </>,
  );
};
init();
